import i18next, { t } from 'i18next';
import intervalPlural from 'i18next-intervalplural-postprocessor';
import { DEFAULT_LANGUAGE, FALLBACK_LANGUAGE } from './constants';
import { Language, de, en, fr, nl } from './_autogenerated_';

/**
 * All available translations, mapped per language.
 */
export const TRANSLATION_RESOURCES = {
  nl: {
    translation: nl,
  },
  en: {
    translation: en,
  },
  fr: {
    translation: fr,
  },
  de: {
    translation: de,
  },
};

const globalContext: Record<string, unknown> = {};

/**
 * Sets a global context value for translations.
 * @param key The key to set.
 * @param value The value to set.
 */
export const setGlobalContextValue = (key: string, value: unknown): void => {
  globalContext[key] = value;
};

/**
 * Default translation configuration.
 */
export const translationConfig = () => ({
  lng: DEFAULT_LANGUAGE,
  fallbackLng: FALLBACK_LANGUAGE,
  resources: TRANSLATION_RESOURCES,
});

/**
 * Initializes the translation library.
 */
export const initializeTranslations = () => {
  if (!i18next.isInitialized) {
    i18next.use(intervalPlural).init(translationConfig());
  }
};

/**
 * Translates the given key to the current language.
 *
 * @param key Translation key as defined in the translation files.
 * @param options Options to pass to the translation function.
 * @returns The translated string (or error message).
 */
export const translate = (key: string, options = {}) => {
  initializeTranslations();

  return t(key, {
    defaultValue: `Missing translation for key: ${key}`,
    lng: i18next.language ?? DEFAULT_LANGUAGE,
    ...globalContext,
    ...options,
  });
};

/**
 * Whether the given key has a translation in the current language.
 *
 * @param key Translation key as defined in the translation files.
 * @returns True when the key resolves to a translation.
 */
export const translationExists = (key: string): boolean => {
  initializeTranslations();

  return i18next.exists(key, {
    lng: i18next.language ?? DEFAULT_LANGUAGE,
  });
};

/**
 * Listen for language changes.
 *
 * @param callback Function to execute when the language changes.
 */
export const onLanguageChanged = (callback: (lng: string) => void) => {
  i18next.on('languageChanged', callback);
};

/**
 * Changes the current language.
 * @param language The language to change to.
 */
export const changeLanguage = (language: Language) => {
  i18next.changeLanguage(language);
};
